import {
  addDays,
  endOfDay,
  isToday,
  setHours,
  startOfDay,
  subDays,
} from 'date-fns';
import { v4 as uuid } from 'uuid';

import { Filter } from '~/evifields/src/types/Filter';
import { OperatorId } from '~/evifields/src/types/OperatorId';
import { AuditLogRecord } from '~/features/audit-logs/types';
import type { useFormatDateReturnType } from '~/i18n/hooks/useFormatDate/types';
import { earlyAccess } from '~/services/serviceInstances';
import { Nullable } from '~/types/Nullable';
import { sortStrings } from '~/utils/sortStrings';

import {
  AuditlogRecordsPayload,
  SchemasAuditlogRecordsResponse,
} from '../../types/eviaudit/records';

interface Request {
  filters: Filter[];
  pageIndex: number;
  pageSize: number;
  sortBy?: {
    id: keyof AuditLogRecord;
    desc: boolean;
  };
  //** The next token to be used to retrieve the next batch of auditlog entries */
  next?: null | string;
  formatDate: useFormatDateReturnType;
}

type Response = SchemasAuditlogRecordsResponse;

const MAX_AUDIT_LOGS_LIMIT = 50000;

export const getAuditLogs = async (
  request: Request,
): Promise<{
  results: AuditLogRecord[];
  totalCount: number;
  hasMore: boolean;
}> => {
  const postData: AuditlogRecordsPayload = mapRequestToPostData(request);

  const responses: Response[] = [];
  let totalCount = 0;
  let hasMoreRecords = false;

  while (totalCount < MAX_AUDIT_LOGS_LIMIT) {
    const response: Response = await earlyAccess.post(
      '/auditlog/records',
      postData,
    );

    const { data, has_more, next } = response;
    postData.next = next;

    const countToAdd = Math.min(MAX_AUDIT_LOGS_LIMIT - totalCount, data.length);
    responses.push({
      ...response,
      data: data.slice(0, countToAdd),
    });
    totalCount += countToAdd;
    hasMoreRecords = has_more === true || totalCount >= MAX_AUDIT_LOGS_LIMIT;

    // Break early if there are no more batches
    if (has_more === false) {
      break;
    }
  }

  return prepareResponse(request, responses.flat(), hasMoreRecords);
};

const prepareResponse = (
  request: Request,
  responses: Response[],
  hasMore: boolean,
): {
  results: AuditLogRecord[];
  totalCount: number;
  hasMore: boolean;
} => {
  const response = responses.reduce(
    (acc, res) => {
      acc.data.push(...res.data);
      return acc;
    },
    { data: [] as Response['data'] },
  );

  const { pageIndex, pageSize, sortBy = '' } = request;
  const startIndex = (pageIndex - 1) * pageSize;
  const endIndex = startIndex + pageSize;

  let results = response.data.map((result) => {
    const { operation, timestamp, details, initiator, entity } = result;

    return {
      id: uuid(),
      activity: operation,
      datetime: timestamp,
      details: stringifyData(details ?? {}),
      initiatorType: initiator?.type,
      initiatorId: initiator?.id,
      initiatorName: initiator?.name,
      recordType: entity?.type,
      recordId: entity?.id,
      recordName: entity?.name,
    };
  });

  if (sortBy) {
    const { id: sortColumn, desc: isSortDesc } = sortBy;

    results = results.sort((a, b) => {
      return isSortDesc
        ? //TODO: only works if text is in lowercase, need to fix
          // use platform-recommended sortStrings util
          sortStrings(String(b[sortColumn]), String(a[sortColumn]))
        : sortStrings(String(a[sortColumn]), String(b[sortColumn]));
    });
  }

  return {
    results: results.slice(startIndex, endIndex),
    totalCount: results.length,
    hasMore,
  };
};

const mapRequestToPostData = (request: Request): AuditlogRecordsPayload => {
  const formatDate = request.formatDate;
  const postData = request.filters.reduce(
    (
      params,
      filter: {
        fieldId: string;
        values: any[];
        operatorId: Nullable<OperatorId>;
      },
    ) => {
      const { fieldId, values, operatorId } = filter;
      switch (fieldId) {
        case 'datetime':
          const [date1, date2] = values;
          const today = new Date();

          const minEndDate = (date: Date) => {
            return isToday(date) ? today : endOfDay(date);
          };

          const maxStartDate = (date: Date) => {
            return isToday(date) ? startOfDay(date) : date;
          };

          let startDate;
          let endDate;

          switch (operatorId) {
            case 'date_after':
              startDate = addDays(date1, 1);
              endDate = today;
              break;
            case 'date_before':
              startDate = subDays(today, 179);
              endDate = date1;
              break;
            case 'date_on':
              startDate = setHours(date1, 0);
              endDate = minEndDate(date1);
              break;
            case 'date_between':
            default:
              startDate = maxStartDate(date1);
              endDate = minEndDate(date2);
              break;
          }

          params.start_time = formatDate(startDate, 'utc');
          params.end_time = formatDate(endDate, 'utc');
          break;
        case 'activity':
          params.operation = values[0];
          break;
        case 'entityType':
          params.entity = values[0];
          break;
      }

      return params;
    },
    {
      operation: 'VIEW',
      entity: 'DOCUMENT',
      start_time: formatDate(new Date(), 'utc'),
      end_time: null,
      next: null,
    } as AuditlogRecordsPayload,
  );

  return postData;
};

const stringifyData = (item: object) => {
  return Object.entries(item)
    .map(([key, value]) => `${key}: ${JSON.stringify(value, null, 2)}`)
    .join(', ');
};
